import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import type { AlertView } from "../../shared/types.ts";
import { Button, Field, Input, Logo, MarkLoader, useToast } from "../ui/index.ts";
import { ApiError, getAlert, saveAlert } from "../lib/api.ts";
import { jobsQuery } from "./PublicJobs.tsx";
import { Column } from "./Unsubscribe.tsx";

type Stage = { at: "loading" } | { at: "down" } | { at: "invalid" } | { at: "edit"; alert: AlertView };

const link = "font-semibold text-primary-ink underline-offset-4 hover:underline";

/**
 * A job alert email's "Edit your alert" link, signed in or out: the alert's role and location, saved with the link's token,
 * and a link to unsubscribe with the same token.
 */
export function AlertEdit() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [token] = useState(() => new URLSearchParams(location.search).get("t") ?? "");
  const [stage, setStage] = useState<Stage>(() => (token ? { at: "loading" } : { at: "invalid" }));
  const [q, setQ] = useState("");
  const [where, setWhere] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const show = (alert: AlertView) => {
    setStage({ at: "edit", alert });
    setQ(alert.q);
    setWhere(alert.location ?? "");
  };

  const load = useCallback(() => {
    if (!token) return;
    setStage({ at: "loading" });
    getAlert(token).then(show, (e) => setStage(e instanceof ApiError && e.status < 500 ? { at: "invalid" } : { at: "down" }));
  }, [token]);

  useEffect(load, [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (q.trim().length < 2) return setError("Enter a role, like Software Engineer Intern.");
    setBusy(true);
    setError(null);
    try {
      show(await saveAlert(token, { q: q.trim(), location: where.trim() || null }));
      toast("Your job alert was updated.", "ok");
    } catch (err) {
      if (err instanceof ApiError && err.error === "bad_search") setError("This search can't be saved as an alert. Try a different one.");
      else if (err instanceof ApiError && err.status < 500 && err.status !== 429) setStage({ at: "invalid" });
      else setError("Your alert didn't save. Try again.");
    }
    setBusy(false);
  };

  let page: ReactNode;
  if (stage.at === "loading") page = <MarkLoader delay={0.4} />;
  else if (stage.at === "down")
    page = (
      <Column step="down" title="Something went wrong." text="Check your connection and try again.">
        <Button size="lg" variant="secondary" onClick={load}>
          Try again
        </Button>
      </Column>
    );
  else if (stage.at === "invalid")
    page = (
      <Column step="invalid" title="This link is invalid or has expired." text="Search for jobs to set up a new job alert.">
        <Button size="lg" onClick={() => navigate("/jobs")}>
          Browse jobs
        </Button>
      </Column>
    );
  else
    page = (
      <Column
        step="edit"
        title="Edit your job alert."
        text={
          <>
            {stage.alert.active ? "Job alerts go to " : "You're unsubscribed. Save to start sending job alerts to "}
            <span className="font-semibold text-ink-soft">{stage.alert.email}</span>
            {stage.alert.active ? "." : " again."}
          </>
        }
      >
        <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
          <Field label="Role">
            <Input value={q} onChange={(e) => setQ(e.target.value)} maxLength={80} autoComplete="off" />
          </Field>
          <Field label="Location">
            <Input value={where} onChange={(e) => setWhere(e.target.value)} maxLength={80} autoComplete="off" placeholder="Any location" />
          </Field>
          {error && (
            <p className="text-sm text-bad-ink" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" size="lg" className="mt-1" loading={busy}>
            Save changes
          </Button>
        </form>
        <p className="flex flex-wrap gap-x-5 gap-y-2 text-[15px]">
          <Link href={`/jobs${jobsQuery(stage.alert.q, stage.alert.location ?? "")}`} className={link}>
            See jobs for this alert
          </Link>
          {stage.alert.active && (
            <Link href={`/unsubscribe?t=${encodeURIComponent(token)}`} className={link}>
              Unsubscribe
            </Link>
          )}
        </p>
      </Column>
    );

  return (
    <main className="flex min-h-dvh flex-col bg-page px-5 pb-8 pt-6 md:px-10">
      <Link href="/" aria-label="headcount home" className="self-start rounded-control">
        <Logo size={22} />
      </Link>
      <div className="grid flex-1 place-items-center pb-16">{page}</div>
    </main>
  );
}
